import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  Typography,
  MenuItem,
  Chip,
  Divider
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ModalForms from '../Shared/Modals';

const HotLeadCriteriaModal = ({ open, onClose, onSave, hotLeadFields, dropdownOptions, title = '' }) => {
  const [criteriaList, setCriteriaList] = useState([]);
  const [inputValues, setInputValues] = useState({});

  const handleAddCriteria = () => {
    setCriteriaList((prev) => [
      ...prev,
      {
        name: `is_hot_level_${prev.length + 1}`,
        alias: '',
        operation: [{
          name: 'or',
          items: []
        }]
      }
    ]);
  };

  const handleAliasChange = (index, value) => {
    const updated = [...criteriaList];
    updated[index].alias = value;
    setCriteriaList(updated);
  };

  // const handleFieldChange = (criteriaIdx, opIdx, fieldId) => {
  //   const updated = [...criteriaList];
  //   updated[criteriaIdx].operation[opIdx].items.push({ id: fieldId, values: [] });
  //   setCriteriaList(updated);
  // };

  const handleFieldChange = (criteriaIdx, opIdx, fieldId) => {
    const updated = [...criteriaList];
    const optionsForField = dropdownOptions.find(opt => opt.key_name === hotLeadFields.find(f => f.item_id == fieldId)?.key_name)?.key_options || [];

    updated[criteriaIdx].operation[opIdx].items.push({
      id: fieldId,
      values: [],
      options: optionsForField // ← Add options here
    });
    setCriteriaList(updated);
  };


  // const handleValueAdd = (criteriaIdx, opIdx, itemIdx, value) => {
  //   if (!value.trim()) return;
  //   const updated = [...criteriaList];
  //   const item = updated[criteriaIdx].operation[opIdx].items[itemIdx];
  //   if (!item.values.includes(value)) item.values.push(value);
  //   setCriteriaList(updated);
  // };

  const handleValueAdd = (criteriaIdx, opIdx, itemIdx, value) => {
    if (!value.trim()) return;

    const updated = [...criteriaList];
    const item = updated[criteriaIdx].operation[opIdx].items[itemIdx];
    if (!item.values.includes(value)) {
      item.values.push(value);
    }
    setCriteriaList(updated);

    // Clear the input field for that specific input
    const inputKey = `${criteriaIdx}-${opIdx}-${itemIdx}`;
    setInputValues(prev => ({ ...prev, [inputKey]: '' }));
  };


  const handleValueDelete = (criteriaIdx, opIdx, itemIdx, valueIdx) => {
    const updated = [...criteriaList];
    updated[criteriaIdx].operation[opIdx].items[itemIdx].values.splice(valueIdx, 1);
    setCriteriaList(updated);
  };

  const handleRemoveField = (criteriaIdx, opIdx, itemIdx) => {
    const updated = [...criteriaList];
    updated[criteriaIdx].operation[opIdx].items.splice(itemIdx, 1);
    setCriteriaList(updated);
  };

  const handleDeleteCriteria = (index) => {
    const updated = [...criteriaList];
    updated.splice(index, 1);
    setCriteriaList(updated);
  };

  return (
    <ModalForms
      show={open}
      handleClose={() => onClose(null, 'cancel')}
      modalHeader={'Global Hot Lead Criteria'}
      modalBody={
        <Box sx={{ mt: 2 }}>
          {criteriaList.map((criteria, cIdx) => (
            <Box key={`criteria-${cIdx}`} sx={{ mb: 3, p: 2, border: '1px solid #ccc', borderRadius: 2 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle1">Hotness Level Criteria {cIdx + 1}</Typography>
                <IconButton onClick={() => handleDeleteCriteria(cIdx)}>
                  <DeleteIcon />
                </IconButton>
              </Box>

              <TextField
                label="Alias"
                fullWidth
                size="small"
                value={criteria.alias}
                onChange={(e) => handleAliasChange(cIdx, e.target.value)}
                sx={{ my: 2 }}
              />

              {criteria.operation.map((op, opIdx) => (
                <Box key={`op-${opIdx}`}>
                  <TextField
                    select
                    label="Select Field"
                    size="small"
                    value=""
                    onChange={(e) => handleFieldChange(cIdx, opIdx, e.target.value)}
                    sx={{ width: 300, mb: 2 }}
                  >
                    <MenuItem value="">Select Field</MenuItem>
                    {hotLeadFields
                      .filter(field => {
                        return !criteria.operation.some(op =>
                          op.items.some(item => item.id === field.item_id)
                        );
                      })
                      .map((field) => (
                        <MenuItem key={field.item_id} value={field.item_id}>
                          {field.key_name}
                        </MenuItem>
                      ))}
                  </TextField>

                  {op.items.map((item, iIdx) => (
                    <Box key={`item-${iIdx}`} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle2" sx={{ whiteSpace: 'nowrap' }}>
                          Field: {hotLeadFields.find(f => f.item_id == item.id)?.key_name || item.id}
                        </Typography>

                        {item.options?.length > 0 && (
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {item.options.map((opt, idx) => (
                              <Chip
                                key={idx}
                                label={opt}
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                  const inputKey = `${cIdx}-${opIdx}-${iIdx}`;
                                  setInputValues(prev => ({ ...prev, [inputKey]: opt }));
                                }}
                                sx={{ cursor: 'pointer' }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>

                      <Box display="flex" gap={2} alignItems="center" sx={{ mt: 1 }}>
                        <TextField
                          size="small"
                          label="Enter Value"
                          value={inputValues[`${cIdx}-${opIdx}-${iIdx}`] || ''}
                          onChange={(e) =>
                            setInputValues(prev => ({
                              ...prev,
                              [`${cIdx}-${opIdx}-${iIdx}`]: e.target.value
                            }))
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleValueAdd(cIdx, opIdx, iIdx, e.target.value);
                            }
                          }}
                        />

                        <Button
                          variant="contained"
                          size="small"
                          onClick={() =>
                            handleValueAdd(cIdx, opIdx, iIdx, inputValues[`${cIdx}-${opIdx}-${iIdx}`] || '')
                          }
                        >
                          Add
                        </Button>
                      </Box>

                      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {item.values.map((val, vIdx) => (
                          <Chip
                            key={vIdx}
                            label={val}
                            onDelete={() => handleValueDelete(cIdx, opIdx, iIdx, vIdx)}
                            color="primary"
                          />
                        ))}
                      </Box>

                      <IconButton color="error" onClick={() => handleRemoveField(cIdx, opIdx, iIdx)}>
                        <DeleteIcon />
                      </IconButton>

                      <Divider sx={{ my: 2 }} />
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          ))}

          <Button onClick={handleAddCriteria} variant="outlined">
            Add More Criteria
          </Button>
        </Box>
      }

      modalId={title}
      size="md"
      modalFooter={
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button onClick={() => onClose(null, 'cancel')} variant="outlined" size="small">
            Cancel
          </Button>
          <Button
            onClick={() => onSave(criteriaList)}
            variant="contained"
            color="primary"
            size="small"
          >
            Save
          </Button>
        </Box>
      }
    />
  );
};

export default HotLeadCriteriaModal;
